"use client";
import React, { useEffect, useRef, useState } from "react";
import { asText } from "@prismicio/client";
import clsx from "clsx";
import { PrismicRichText } from "@prismicio/react";

const titleComponent = {
  heading5: ({ children }) => (
    <h5 className="text-medium text-[#232629]">{children}</h5>
  ),
  heading6: ({ children }) => (
    <h6 className="text-medium text-[#232629]">{children}</h6>
  ),
  paragraph: ({ children }) => (
    <p className="text-medium text-[#232629]">{children}</p>
  ),
};

const contentComponent = {
  paragraph: ({ children }) => (
    <p className="text-base text-[#232629]">{children}</p>
  ),
  strong: ({ children }) => (
    <strong className="text-base text-[#232629] font-semibold">
      {children}
    </strong>
  ),
  list: ({ children }) => (
    <ul className="text-base text-[#232629] pl-4 mb-7 last:mb-0 md:pl-6">
      {children}
    </ul>
  ),
  listItem: ({ children }) => (
    <li className="text-base text-[#232629] pl-1 mb-1 list-disc last:mb-0 md:pl-2">
      {children}
    </li>
  ),
};

const QuestionBlocks = ({ list }) => {
  const [currentItem, setCurrentItem] = useState("");

  return (
    <div className={"mt-[30px] md:mt-[40px] xl:mt-[56px]"}>
      {list.map(({ question, answer }, idx) => (
        <Accordion
          isFirst={idx === 0}
          title={question}
          content={answer}
          key={idx}
          currentItem={currentItem}
          setCurrentItem={setCurrentItem}
        />
      ))}
    </div>
  );
};

export default QuestionBlocks;

function Accordion({
  title,
  content,
  currentItem,
  setCurrentItem,
  isFirst = false,
}) {
  const expanded = currentItem === asText(title);
  const [height, setHeight] = useState(0);

  const toggleExpanded = () => {
    if (currentItem === asText(title)) {
      setCurrentItem("");
    } else {
      setCurrentItem(asText(title));
    }
  };

  const ref = useRef(null);

  useEffect(() => {
    setHeight(ref.current.clientHeight);
  });

  return (
    <div
      role={"presentation"}
      className={clsx({
        "cursor-pointer bg-[#F4F4F4]": true,
        "mt-[10px] md:mt-[12px]": !isFirst,
      })}
      onClick={toggleExpanded}
    >
      <div
        className={clsx({
          "flex flex-row items-center justify-between py-[14px] px-[20px] md:py-[20px] md:px-[30px] text-left select-none gap-[20px]": true,
          "border-b-[0.5px] border-[#B6B6B6]": expanded,
        })}
      >
        <PrismicRichText field={title} components={titleComponent} />
        <div
          className={clsx({
            "relative flex justify-center items-center rounded-[4px] w-full max-w-[28px] h-[28px]": true,
            "border-[1px] border-[#1F2223]": !expanded,
            "bg-[#EEB978]": expanded,
          })}
        >
          <div className={"w-[13.5px] h-[2px] bg-[#1F2223]"} />
          <div
            className={clsx({
              "transition-all  duration-200 absolute top-[50%] left-[50%] -mt-[6px] -ml-[1px] w-[2px] h-[13.5px] bg-[#1F2223]": true,
              "scale-y-1": !expanded,
              "scale-y-0": expanded,
            })}
          />
        </div>
      </div>
      <div
        className={`overflow-hidden transition-[max-height] duration-200 ease-linear`}
        style={{ maxHeight: expanded ? `${height}px` : `0px` }}
      >
        <div ref={ref}>
          <div className="px-[20px] py-[10px] md:px-[30px] md:pt-[20px] md:pb-[30px]">
            <PrismicRichText field={content} components={contentComponent} />
          </div>
        </div>
      </div>
    </div>
  );
}
